import { SourceTags, sourceTagsFromStack } from './sourceTags';

export type LogLevel = 'Error' | 'Warning' | 'Information';

export interface LogContext {
  sourceFile?: string;
  sourceClass?: string;
  sourceLayer?: string;
  [key: string]: unknown;
}

const RESIZE_OBSERVER_NOISE =
  'ResizeObserver loop completed with undelivered notifications';

class LogglyLogger {
  private readonly enabled: boolean;
  private readonly customerToken: string;
  private readonly staticTags: string;
  private readonly ingestUrl: string;
  private sending = false;
  private forwardingConsole = false;

  constructor() {
    this.customerToken = process.env.REACT_APP_LOGGLY_TOKEN ?? '';
    this.staticTags =
      process.env.REACT_APP_LOGGLY_TAGS ?? 'production,carbon-monitor-app';
    this.ingestUrl =
      process.env.REACT_APP_LOGGLY_URL ?? 'https://logs-01.loggly.com/inputs';
    this.enabled =
      !!this.customerToken &&
      (process.env.NODE_ENV === 'production' ||
        process.env.REACT_APP_LOGGLY_ENABLED === 'true');
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    this.log('Error', message, error, context);
  }

  warning(message: string, error?: unknown, context?: LogContext): void {
    this.log('Warning', message, error, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('Information', message, undefined, context);
  }

  logFromConsole(level: 'error' | 'warn', args: unknown[]): void {
    const mappedLevel = level === 'error' ? 'Error' : 'Warning';
    const message = args
      .map((arg) => {
        if (arg instanceof Error) {
          return arg.message;
        }
        if (typeof arg === 'string') {
          return arg;
        }
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');

    if (message.includes(RESIZE_OBSERVER_NOISE)) {
      return;
    }

    const errorArg = args.find((arg) => arg instanceof Error);
    if (this.forwardingConsole) {
      return;
    }
    this.forwardingConsole = true;
    try {
      this.log(mappedLevel, message, errorArg, undefined, 4);
    } finally {
      this.forwardingConsole = false;
    }
  }

  private log(
    level: LogLevel,
    message: string,
    error?: unknown,
    context?: LogContext,
    stackSkipFrames = 3
  ): void {
    if (message.includes(RESIZE_OBSERVER_NOISE)) {
      return;
    }

    const stack =
      error instanceof Error
        ? error.stack
        : new Error().stack ?? undefined;

    const parsedTags = context?.sourceFile
      ? {
          SourceFile: context.sourceFile,
          SourceClass: context.sourceClass ?? context.sourceFile.replace(/\.(tsx?|jsx?)$/, ''),
          SourceLayer: context.sourceLayer ?? 'App',
          tags: [
            context.sourceFile,
            context.sourceClass ?? context.sourceFile.replace(/\.(tsx?|jsx?)$/, ''),
            context.sourceLayer ?? 'App',
          ],
        }
      : sourceTagsFromStack(stack, stackSkipFrames);

    const payload = {
      level,
      message,
      Application: 'carbon-monitor-app',
      SourceFile: parsedTags.SourceFile,
      SourceClass: parsedTags.SourceClass,
      SourceLayer: parsedTags.SourceLayer,
      tags: [...parsedTags.tags, ...this.staticTags.split(',').map((t) => t.trim())],
      url: window.location.href,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...(error instanceof Error
        ? {
            exception: error.name,
            exceptionMessage: error.message,
            stack: error.stack,
          }
        : error
          ? { exceptionDetail: String(error) }
          : {}),
      ...(context
        ? Object.fromEntries(
            Object.entries(context).filter(
              ([key]) => !['sourceFile', 'sourceClass', 'sourceLayer'].includes(key)
            )
          )
        : {}),
    };

    if (this.enabled) {
      this.send(payload);
    }
  }

  private send(payload: Record<string, unknown>): void {
    if (this.sending) {
      return;
    }

    const tagPath = this.staticTags
      .split(',')
      .map((tag) => encodeURIComponent(tag.trim()))
      .join(',');
    const url = `${this.ingestUrl}/${encodeURIComponent(this.customerToken)}/tag/${tagPath}`;
    const body = JSON.stringify(payload);

    try {
      this.sending = true;
      // Do not use sendBeacon here — cross-origin beacons use credentials mode
      // "include", which fails against Loggly's CORS headers during local testing.
      // text/plain + no-cors avoids a CORS preflight from the browser.
      void fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        mode: 'no-cors',
        credentials: 'omit',
      }).catch(() => {
        // Avoid recursive console logging if Loggly is unreachable.
      });
    } finally {
      this.sending = false;
    }
  }
}

export const logger = new LogglyLogger();
export type { SourceTags };
